/**
 * Co-located TanStack Query hooks for the COM client portal. Bound to the shared
 * credentialed client (`http` from @teivor/shared-utils/api) which sends cookies,
 * unwraps the `{ success, data }` envelope, and maps 401/403/409/410/422 to typed
 * errors. Every endpoint is `/api/com/…` (S1–S3) except the Account screen (S4),
 * whose machinery lives in `iam` (`/api/iam/account/*`) — the spec's thin
 * `/api/com/account` proxy is not yet built, so we bind directly to the real
 * source rather than fabricate one.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { http, NotFoundError } from '@teivor/shared-utils/api';

import type {
  CampaignApproval,
  ChatMessage,
  ChatThread,
  ClientDashboardResponse,
  ConfirmApprovalBody,
  DashboardPeriod,
  InvoiceRecord,
  PortalAccount,
} from './types';

// ─── Query keys ─────────────────────────────────────────────────────────────

export const comKeys = {
  dashboard: (period: DashboardPeriod) => ['com', 'dashboard', period] as const,
  invoices: () => ['com', 'dashboard', 'invoices'] as const,
  approvals: () => ['com', 'approvals'] as const,
  approval: (campaignId: string) => ['com', 'approvals', campaignId] as const,
  chatThread: () => ['com', 'chat', 'thread'] as const,
  chatMessages: () => ['com', 'chat', 'messages'] as const,
  account: () => ['com', 'account'] as const,
};

// ─── S1 Dashboard ───────────────────────────────────────────────────────────

/**
 * A workspace whose singleton dashboard view hasn't been provisioned yet (the
 * provisioning processor hasn't run / a freshly-seeded workspace) is an honest
 * "being set up" state, NOT a transport failure. Render the page with every
 * panel in its own empty/unavailable state (HP-EXP-1) rather than nuking the
 * whole screen with a hard error. Any non-404 still surfaces as a real error.
 */
const UNPROVISIONED_DASHBOARD: ClientDashboardResponse = {
  status: 'active',
  activatedAt: '',
  funnel: { available: false, convertedFunnelVersionRef: null },
  liveStatus: { available: false, weeks: [] },
  invoices: { available: false, records: [], emptyReason: 'not_provisioned' },
};

export function useDashboardQuery(period: DashboardPeriod) {
  return useQuery<ClientDashboardResponse>({
    queryKey: comKeys.dashboard(period),
    queryFn: async () => {
      try {
        return await http.get<ClientDashboardResponse>('/api/com/client/dashboard', { period });
      } catch (err) {
        if (err instanceof NotFoundError) return UNPROVISIONED_DASHBOARD;
        throw err;
      }
    },
  });
}

export function useInvoicesQuery() {
  return useQuery<InvoiceRecord[]>({
    queryKey: comKeys.invoices(),
    queryFn: () => http.get<InvoiceRecord[]>('/api/com/client/dashboard/invoices'),
  });
}

/** Per-row download. The backend returns 501 until an invoice source is wired. */
export function useDownloadInvoiceMutation() {
  return useMutation<{ url?: string }, unknown, string>({
    mutationFn: (invoiceId: string) =>
      http.get<{ url?: string }>(`/api/com/client/dashboard/invoices/${invoiceId}/download`),
  });
}

// ─── S2 Campaign Approvals ──────────────────────────────────────────────────

export function useApprovalsQuery() {
  return useQuery<CampaignApproval[]>({
    queryKey: comKeys.approvals(),
    queryFn: () => http.get<CampaignApproval[]>('/api/com/client/approvals'),
  });
}

export function useApprovalDetailQuery(campaignId: string | undefined) {
  return useQuery<CampaignApproval>({
    queryKey: comKeys.approval(campaignId ?? ''),
    queryFn: () =>
      http.get<CampaignApproval>(`/api/com/client/approvals/${campaignId}`),
    enabled: Boolean(campaignId),
  });
}

export function useConfirmApprovalMutation() {
  const qc = useQueryClient();
  return useMutation<CampaignApproval, unknown, { approvalId: string; body: ConfirmApprovalBody }>({
    mutationFn: ({ approvalId, body }) =>
      http.post<CampaignApproval>(`/api/com/client/approvals/${approvalId}/confirm`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: comKeys.approvals() });
    },
  });
}

// ─── S3 Client Chat ─────────────────────────────────────────────────────────

export function useChatThreadQuery() {
  return useQuery<ChatThread>({
    queryKey: comKeys.chatThread(),
    queryFn: () => http.get<ChatThread>('/api/com/client/chat'),
  });
}

export function useChatMessagesQuery() {
  return useQuery<ChatMessage[]>({
    queryKey: comKeys.chatMessages(),
    queryFn: () => http.get<ChatMessage[]>('/api/com/client/chat/messages'),
  });
}

export function useSendMessageMutation() {
  const qc = useQueryClient();
  return useMutation<ChatMessage, unknown, { body: string }>({
    mutationFn: ({ body }) =>
      http.post<ChatMessage>('/api/com/client/chat/messages', { body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: comKeys.chatMessages() });
      void qc.invalidateQueries({ queryKey: comKeys.chatThread() });
    },
  });
}

export function useMarkChatReadMutation() {
  const qc = useQueryClient();
  return useMutation<ChatThread, unknown, { upToMessageId: string }>({
    mutationFn: ({ upToMessageId }) =>
      http.patch<ChatThread>('/api/com/client/chat/read', { upToMessageId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: comKeys.chatThread() });
    },
  });
}

// ─── S4 Account (iam-backed) ────────────────────────────────────────────────

export function useAccountQuery() {
  return useQuery<PortalAccount>({
    queryKey: comKeys.account(),
    queryFn: () => http.get<PortalAccount>('/api/iam/account'),
  });
}

export function useUpdateProfileMutation() {
  const qc = useQueryClient();
  return useMutation<PortalAccount, unknown, { displayName: string; version: number }>({
    mutationFn: (body) => http.patch<PortalAccount>('/api/iam/account/profile', body),
    onSuccess: (data) => {
      qc.setQueryData(comKeys.account(), data);
    },
  });
}

export function useUpdateLanguageMutation() {
  const qc = useQueryClient();
  return useMutation<
    PortalAccount,
    unknown,
    { preferredLanguage: 'cs-CZ' | 'en-US'; version: number }
  >({
    mutationFn: (body) => http.patch<PortalAccount>('/api/iam/account/preferences', body),
    onSuccess: (data) => {
      qc.setQueryData(comKeys.account(), data);
    },
  });
}

export function useChangePasswordMutation() {
  return useMutation<
    { success: true },
    unknown,
    { currentPassword: string; newPassword: string; confirmPassword: string }
  >({
    mutationFn: (body) => http.post<{ success: true }>('/api/iam/account/password', body),
  });
}

export function useEnrollMfaMutation() {
  return useMutation<{ otpauthUri: string; secretRef: string }, unknown, void>({
    mutationFn: () =>
      http.post<{ otpauthUri: string; secretRef: string }>('/api/iam/account/mfa/enroll'),
  });
}

export function useVerifyMfaMutation() {
  const qc = useQueryClient();
  return useMutation<{ backupCodes: string[] }, unknown, { code: string }>({
    mutationFn: (body) => http.post<{ backupCodes: string[] }>('/api/iam/account/mfa/verify', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: comKeys.account() });
    },
  });
}

export function useDisableMfaMutation() {
  const qc = useQueryClient();
  return useMutation<{ success: true }, unknown, { code: string }>({
    mutationFn: (body) => http.post<{ success: true }>('/api/iam/account/mfa/disable', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: comKeys.account() });
    },
  });
}
